import { Notice, StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { classify, type PhValues } from "@/lib/tool-kit/chem-reactions/ph";
import {
  formatFixed,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";

/** English strings for the four-value result. The pH widgets spread these in. */
export const PH_STATS_STRINGS = {
  ph: "pH",
  poh: "pOH",
  hydrogen: "[H⁺] (mol/L)",
  hydroxide: "[OH⁻] (mol/L)",
  acidic: "This solution is acidic: pH below 7 at 25 °C.",
  neutral: "This solution is neutral: pH 7 at 25 °C.",
  basic: "This solution is basic: pH above 7 at 25 °C.",
  kwNote:
    "All values are at 25 °C, where Kw = [H⁺][OH⁻] = 1.0 × 10⁻¹⁴ and pH + pOH = 14.00. Kw changes with temperature, and so does neutral pH.",
} as const;

type StatsStrings = Record<keyof typeof PH_STATS_STRINGS, string>;

/**
 * pH, pOH, [H⁺] and [OH⁻] as cards. `decimals` is the number of decimal places of the pH
 * and pOH; `sig` the significant figures of the concentrations. By the usual convention the
 * two match, because the digits after the point in a pH are the significant figures of the
 * concentration it came from.
 */
export function PhStats({
  values,
  decimals,
  sig,
  strings,
  fmt,
}: {
  values: PhValues;
  decimals: number;
  sig: number;
  strings: StatsStrings;
  fmt: NumberFormatter;
}) {
  const reaction = classify(values.pH);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.ph}
          value={formatFixed(fmt, values.pH, decimals)}
        />
        <StatCard
          label={strings.poh}
          value={formatFixed(fmt, values.pOH, decimals)}
        />
        <StatCard
          label={strings.hydrogen}
          value={formatSig(fmt, values.hydrogen, sig)}
        />
        <StatCard
          label={strings.hydroxide}
          value={formatSig(fmt, values.hydroxide, sig)}
        />
      </StatGrid>
      <Notice>{strings[reaction]}</Notice>
      <p className="text-muted-foreground text-xs">{strings.kwNote}</p>
    </>
  );
}
